import {
  compact,
  filter,
  find,
  get,
  isEmpty,
  orderBy,
  pickBy,
  remove,
  size,
  some,
  uniq,
  uniqBy
} from 'lodash';
import { createSelector } from '@reduxjs/toolkit';

import { activityTypes } from 'constants/activities';
import { applicationProperties, applicationTypes, scriptTypes } from 'constants/scripts';
import { inputStates } from 'constants/values';

// Selectors
import {
  getActivityByActivityType,
  isActivityPrimaryScript
} from 'activities/selectors';
import {
  getActiveFieldId,
  getActiveFieldUuid,
  getActiveScriptType
} from 'current/selectors';
import {
  getEditSeedSummary,
  getManageSeedSummary,
  getPlantingScriptsForManageScriptList,
  hasMissingSeedProducts,
  hasMissingSeedRates
} from 'scripts/planting-selectors';
import {
  getApplicationScriptsForManageScriptList,
  getEditApplicationSummary,
  getManageApplicationSummary
} from 'scripts/application-selectors';
import {
  fertilityScriptTypeIsNitrogen,
  getEditFertilizerSummary,
  getFertilityScriptsForManageScriptList,
  getManageFertilizerSummary,
  hasMissingFertilizerProducts,
  hasMissingFertilizerRates
} from 'scripts/fertility-selectors';

import { getZoneHistorySnapshotCount } from 'scripts/drawing-history/selectors';
import { hasFertilizerApplicationsForZones } from 'scripts/zones/fertility-selectors';
import { getProductApplicationProductsInZones } from 'scripts/zones/productApplication-selectors';
import { hasSeedApplicationsForZones } from 'scripts/zones/planting-selectors';
import {
  getProductYearByScriptType
} from 'settings/selectors';
import {
  getRenameScriptValidationResult,
  getScriptValidationResult
} from 'validations/selectors';

import { dateIsSameOrAfter } from 'util/formatters/dateFormatter';
import { getBagSizeForCrop } from 'user/selectors';
import { isResourceUnloaded } from 'loading/selectors';
import { resourceType } from 'constants/loading';
import { getRawScriptList } from 'export-selector/selectors';

export const getActivityTypeForScript = (script) => get(script, 'activity-type', activityTypes.PLANTING);
export const getScriptTypeForScript = (script) => {
  let activityType;
  if (getActivityTypeForScript(script) && getActivityTypeForScript(script).startsWith(scriptTypes.APPLICATION)) {
    activityType = scriptTypes.APPLICATION;
  } else if (getActivityTypeForScript(script) === activityTypes.PLANTING) {
    activityType = scriptTypes.PLANTING;
  } else {
    activityType = scriptTypes.FERTILITY;
  }

  return activityType;
};

export const getCropForScript = (script) => {
  return get(script, 'meta.crop');
};

export const getCropIdForScript = (state, script) => {
  return get(script, 'meta.cropId');
};

export const isPrimaryScript = (script) => script.primary;
export const isScriptTypePlanting = (state) => {
  const scriptType = getActiveScriptType(state);
  return scriptType === scriptTypes.PLANTING;
};
export const getPrimaryScripts = (state) => Object.values(state.scripts).filter(isPrimaryScript);
export const getNumPrimaryScripts = (state) => getPrimaryScripts(state).length;
export const hasMissingProductRates = (script) => {
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return hasMissingSeedRates(script);
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return hasMissingFertilizerRates(script);
    default:
      return null;
  }
};

export const getBagSizeForScript = (state, script) => getBagSizeForCrop(state, getCropForScript(script));

export const hasMissingProducts = (script) => {
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return hasMissingSeedProducts(script);
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return hasMissingFertilizerProducts(script);
    default:
      return null;
  }
};

export const hasUnsavedUpdates = (script) => {
  const { zones, meta } = script;
  return meta.scriptState === inputStates.CHANGED
  || some(zones, (zone) => {
    const { inputState } = zone.meta;
    return inputState === inputStates.CHANGED;
  });
};

export const isInitialScript = (script) => script['fmz-version'] === 0;
export const isCompleteScript = (script) => {
  const fmzVersionIsValid = script['fmz-version'] >= 1;
  return !hasMissingProducts(script) && !hasMissingProductRates(script) && fmzVersionIsValid;
};

export const getSeasonCodeForScript = (script) => get(script, 'season');
export const getProductYearFromSeasonCode = (code) => code.substr(0,4);
export const getProductYearForScript = (script) => {
  return get(script, 'cropping-year') || getProductYearFromSeasonCode(getSeasonCodeForScript(script));
};
export const scriptIsInProductYear = (script, productYear) => {
  return getProductYearForScript(script) === productYear.toString(); // in case  is passed as a num
};

// Will we display a script as a primary script in the UI?
export const isDisplayPrimaryScript = (state, script) => {
  const scriptType = getScriptTypeForScript(script);

  /* I think the rationale for this logic is...
     The primary tag reported from Groot (isPrimaryScript) is reliable for planting scripts,
     but for fertility scripts, there may be duplicates immediately after setting a
     primary script in UI, (SC-1694) so checking the activities (isActivityPrimaryScript)
     avoids the duplicate problem. */
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return isPrimaryScript(script)
        && isCompleteScript(script);
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return isPrimaryScript(script)
        && isCompleteScript(script)
        && isActivityPrimaryScript(state, script.id);
    default:
      return null;
  }
};
export const isPastProductYearScript = (state, script) => {
  const scriptType = getScriptTypeForScript(script);
  const currentProductYear = getProductYearByScriptType(state, scriptType);
  const scriptProductYear = getProductYearForScript(script);

  return !dateIsSameOrAfter({ anchorDate: currentProductYear, comparisonDate: scriptProductYear });
};
export const canMakeDisplayPrimaryScript = (state, script) => {
  return !isDisplayPrimaryScript(state, script)
    && !isPastProductYearScript(state, script);
};
export const isDisplayPrimaryScriptWhileZonesLoading = (script) => isPrimaryScript(script);
export const getSummaryForScript = (script) => {
  const scriptType = getScriptTypeForScript(script);
  return get(script, `summary.${scriptType}`, []);
};

export const getScriptSummaryProducts = ({ scriptType, script }) => {
  return get(script, `summary.${scriptType}`).map((summaryItem) => summaryItem.product);
};
export const getProductsForScript = (script) => {
  const scriptType = getScriptTypeForScript(script);
  const { meta, zones } = script;
  const zoneApplicationProducts = getProductApplicationProductsInZones({ scriptType, zones });
  if (zoneApplicationProducts.length) {
    return zoneApplicationProducts;
  }
  return get(meta, 'products');
};

export const getScriptById = (state, scriptId) => get(state, `scripts.${scriptId}`);

export const getScriptsForFieldId = (state, fieldId) => {
  const allScripts = get(state, 'scripts', {});
  return pickBy(allScripts, (script) => {
    return get(script, 'meta.fieldId') === fieldId;
  });
};

export const getPrimaryScriptsForFieldId = (state, fieldId) => {
  return Object.values(getScriptsForFieldId(state, fieldId)).filter(isPrimaryScript);
};

export const getScriptsForFieldIdAndProductYear = (state, fieldId, productYear) => {
  return pickBy(getScriptsForFieldId(state, fieldId), (script) => scriptIsInProductYear(script, productYear));
};

export const areActiveFieldScriptsViewable = (state) => {
  const fieldId = getActiveFieldId(state);
  const scriptList = getScriptsForFieldId(state, fieldId);
  if (!isEmpty(scriptList)) {
    return Object.values(scriptList).findIndex( (script) => script.id && script.zones ) !== -1;
  }
  return true;
};

export const getScriptsSortedForManageScriptList = (state, scripts=[]) => {
  // pluck out the display primary - note some fields do not have a primary script
  let displayPrimaryScripts = remove(scripts, (script) => isDisplayPrimaryScript(state, script));
  let displayPrimaryScript = displayPrimaryScripts.length
    ? displayPrimaryScripts[0]
    // Less stringent check while zones are still loading to avoid reordering in most cases.
    : find(scripts, isDisplayPrimaryScriptWhileZonesLoading);

  if (!displayPrimaryScript && scripts && scripts.shift) {
    displayPrimaryScript = scripts.shift();
  }
  // sort the rest of the fields by edit time descending
  const sortedFields = orderBy(scripts, ['update-time'],['desc']);
  // plop the display primary on the top of the list
  sortedFields.unshift(displayPrimaryScript);
  return compact(uniqBy(sortedFields, 'id'));
};

/**
 * Returns a list of scripts to show in the UI based on the script type
 * selected (planting, fertility, or crop protection).
 */
export const getScriptsForManageScriptList = createSelector([
  state => getActiveScriptType(state),
  state => getPlantingScriptsForManageScriptList(state),
  state => getFertilityScriptsForManageScriptList(state),
  state => getApplicationScriptsForManageScriptList(state)
],
(scriptType, plantingScripts, fertilityScripts, applicationScripts) => {
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return plantingScripts;
    case scriptTypes.FERTILITY:
      return fertilityScripts;
    case scriptTypes.APPLICATION:
      return applicationScripts;
    default:
      return [];
  }
});

export const getDisplayPrimaryScriptByActivityType = (state, activityType) => {
  // get all scripts
  const scripts = filter(getScriptsForManageScriptList(state), (script) => {
    return activityType === getActivityTypeForScript(script);
  });
  const [displayPrimaryScript] = filter(scripts, (script) => {
    return isDisplayPrimaryScript(state, script);
  });
  return displayPrimaryScript;
};

export const isAdvancedScript = (script) => get(script, 'tag') === 'auto';
export const isNewAdvancedScript = (script) => isAdvancedScript(script) && isInitialScript(script);

export const getManageProductApplicationSummary = (state, script) => {
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return getManageSeedSummary(script, { bagSize: getBagSizeForScript(state, script)});
    case scriptTypes.FERTILITY:
      return getManageFertilizerSummary(script);
    case scriptTypes.APPLICATION:
      return getManageApplicationSummary(script);
    default:
      return null;
  }
};

export const getEditProductApplicationSummary = (state, script) => {
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return getEditSeedSummary(state, script);
    case scriptTypes.APPLICATION:
      return getEditApplicationSummary(script);
    case scriptTypes.FERTILITY:
      return getEditFertilizerSummary(script);
    default:
      return null;
  }
};

export const scriptIsInvalid = (state, scriptId) => {
  return get(getScriptValidationResult(state, scriptId), 'isInvalid', false)
  || get(getRenameScriptValidationResult(state), 'isInvalid', false);
};

export const getDisplayScriptName = (script) => get(script, 'meta.name');

export const isValidScript = (state, script) => {
  const isInvalid = scriptIsInvalid(state, script.id);
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return !hasMissingSeedProducts(script) && !hasMissingSeedRates(script) && !isInvalid;
    case scriptTypes.FERTILITY:
    case scriptTypes.APPLICATION:
      return !hasMissingFertilizerRates(script) && !isInvalid;
    default:
      return null;
  }
};
export const isEditableScript = (state, script) => !isPastProductYearScript(state, script);
export const isDeleteableScript = (state, script) => {
  return !isDisplayPrimaryScript(state, script);
};
export const isSavedScript = (script) => !hasUnsavedUpdates(script);
export const canSaveScript = (state, script) => isValidScript(state, script) && hasUnsavedUpdates(script);

export const canUndoZoneHistory = (script) => getZoneHistorySnapshotCount(script) > 1;

export const getNumZonesForScript = (script) => size(get(script, 'zones'));
export const getScriptsByActivityType = (state, activityType) => {
  return filter(get(state, 'scripts', []), (script) => getActivityTypeForScript(script) === activityType);
};
export const hasDisplayPrimaryScriptByActivityType = (state, activityType) => {
  const activity = getActivityByActivityType(state, activityType); // should be only one per type right now
  return !!get(activity, 'primary') || !!getDisplayPrimaryScriptByActivityType(state, activityType);
};
export const getApplicationTypeForScriptType = (scriptType) => {
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return applicationTypes.SEED;
    case scriptTypes.FERTILITY:
      return applicationTypes.FERTILIZER;
    case scriptTypes.APPLICATION:
      return applicationTypes.APPLICATION;
    default:
      return null;
  }
};

export const getGeometryViolations = (script) => script.meta.geometryViolations; // Returns undefined if no violations were found.

export const getSummaryPropertiesForScript = (script) => {
  const scriptType = getScriptTypeForScript(script);
  return get(script, `summary.${scriptType}[0].properties`, {});
};

export const getProductYearsForScripts = (state, scripts) => {
  if (!scripts) {
    const fieldId = getActiveFieldId(state);
    scripts = getScriptsForFieldId(state, fieldId);
  }
  let scriptProductYears = Object.values(scripts).map((script) => getProductYearForScript(script));
  Object.values(scriptTypes).forEach((scriptType) => {
    // TODO: does this need to change when we finally iron out cutoff/turnover dates?
    const currentProductYear = getProductYearByScriptType(state, scriptType);
    if (currentProductYear) {
      scriptProductYears.push(currentProductYear.toString());
    }
  });
  return uniq(scriptProductYears).sort();
};

export const hasProductApplicationsForScript = (script) => {
  const { zones } = script;
  const scriptType = getScriptTypeForScript(script);
  switch (scriptType) {
    case scriptTypes.PLANTING:
      return hasSeedApplicationsForZones(zones);
    case scriptTypes.APPLICATION:
    case scriptTypes.FERTILITY:
      return hasFertilizerApplicationsForZones(zones);
    default:
      return null;
  }
};

export const scriptTypeIsNitrogen = (script) => {
  const scriptType = getScriptTypeForScript(script);

  switch (scriptType) {
    case scriptTypes.FERTILITY:
      return fertilityScriptTypeIsNitrogen(script);
    default:
      return false;
  }
};


export const getCropForScriptId = (state, scriptId) => {
  const script = getScriptById(state, scriptId);
  return getCropForScript(script);
};
export const getApplicationTypeByProductApplicationProperty = (state, property) => {
  switch (property) {
    case applicationProperties.FERTILIZER_RATE:
    case applicationProperties.NITROGEN_RATE:
      return applicationTypes.FERTILIZER;
    default:
      return applicationTypes.SEED;
  }
};

export const getProductForScript = script => {
  const product = script 
    && script.meta 
    && script.meta.products
    && script.meta.products.length 
    && script.meta.products[0];
  return product;
};

export const scriptIsOfExportScriptType = (script, exportScriptType) => {
  if ([scriptTypes.FERTILITY, scriptTypes.APPLICATION].includes(exportScriptType)) {
    return script['activity-type'].includes(exportScriptType);
  }
  return script['activity-type'] === exportScriptType;
};

/**
 * @param {object} state 
 * @param {string} fieldUuid 
 * @returns {boolean} Returns if the script summaries weren't loaded for a given field.
 */
export const areRawScriptsForFieldUnLoaded = (state, fieldUuid) => isResourceUnloaded({
  state,
  type: resourceType.RAW_SCRIPTS_BY_FIELD_UUID,
  id: fieldUuid
});

/**
 * Returns the script summaries for the active field, previously requested to groot.
 */
export const getScriptsForActiveFieldFromRawList = createSelector([
  (state) => getRawScriptList(state),
  (state) => getActiveFieldUuid(state)
],
(rawScriptList, fieldUuid) => rawScriptList.filter(({ 'field-uuid': uuid }) => fieldUuid === uuid)
);
